

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredUserType?: 'breeder' | 'buyer' | 'admin';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredUserType 
}) => {
  const { user, profile, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/login');
        return;
      }

      // Check if user type matches required type
      if (profile && requiredUserType && profile.user_type !== requiredUserType) {
        // Don't redirect if the AuthContext is handling the redirect
        // Only redirect for mismatched user types accessing wrong dashboards
        if (location.pathname !== '/pricing') {
          if (profile.user_type === 'admin') {
            navigate('/admin-dashboard');
          } else if (profile.user_type === 'breeder') {
            // Let AuthContext handle breeder redirects (dashboard or pricing)
            return;
          } else {
            const correctDashboard = profile.user_type === 'buyer' 
              ? '/buyer-dashboard' 
              : '/breeder-dashboard';
            navigate(correctDashboard);
          }
        }
        return;
      }
    }
  }, [user, profile, loading, navigate, requiredUserType, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  // Check user type access
  if (profile && requiredUserType && profile.user_type !== requiredUserType) {
    return null;
  }

  return <>{children}</>;
};

